<template>
  <IntercomLayout>
    <!-- Topbar -->
    <template #topbar>
      <IntercomTopbar
        :show-search="false"
        app-name="OnePlaceHQ"
        product-name="Billing"
        :business-name="businessName"
        :user="user"
        @notifications="handleNotifications"
        @settings="handleSettings"
        @logout="handleLogout"
      />
    </template>

    <!-- Sidebar -->
    <template #sidebar>
      <IntercomSidebar :nav-sections="navSections" />
    </template>

    <!-- Main Content -->
    <div class="customers-view">
      <!-- Page Header -->
      <div class="page-header">
        <div>
          <h1 class="page-title">{{ $t('customers.title') }}</h1>
          <p class="page-subtitle">Manage your customer information</p>
        </div>
        <IntercomButton variant="primary" @click="showCreateModal = true">
          <svg
            width="16"
            height="16"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            stroke-width="2"
          >
            <line x1="12" y1="5" x2="12" y2="19" />
            <line x1="5" y1="12" x2="19" y2="12" />
          </svg>
          <span>{{ $t('customers.addCustomer') }}</span>
          <kbd class="shortcut-hint">N</kbd>
        </IntercomButton>
      </div>

      <!-- Search Bar -->
      <div class="search-bar">
        <div class="search-input-wrapper">
          <svg class="search-icon" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
            <circle cx="11" cy="11" r="8" />
            <line x1="21" y1="21" x2="16.65" y2="16.65" />
          </svg>
          <input
            ref="searchInputRef"
            v-model="searchQuery"
            type="text"
            class="search-input"
            :placeholder="$t('common.search') + '...'"
            @input="handleSearchInput"
          />
          <kbd v-if="!searchQuery" class="search-shortcut-hint">/</kbd>
          <button v-if="searchQuery" class="search-clear" @click="clearSearch">
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
              <line x1="18" y1="6" x2="6" y2="18" />
              <line x1="6" y1="6" x2="18" y2="18" />
            </svg>
          </button>
        </div>
      </div>

      <!-- Customers Table -->
      <IntercomCard>
        <IntercomTable
          :columns="columns"
          :data="customers"
          :loading="isLoading"
          :sort-by="sortBy"
          :sort-order="sortOrder"
          :selected-index="selectedIndex"
          hoverable
          clickable
          @row-click="handleEdit"
          @sort="handleSort"
        >
          <template #cell-email="{ value }">
            {{ value || '-' }}
          </template>

          <template #cell-loyalty_points_balance="{ value }">
            <span v-if="value > 0" class="loyalty-badge">
              <svg width="14" height="14" viewBox="0 0 24 24" fill="currentColor">
                <path d="M12 2L15.09 8.26L22 9.27L17 14.14L18.18 21.02L12 17.77L5.82 21.02L7 14.14L2 9.27L8.91 8.26L12 2Z"/>
              </svg>
              {{ Math.floor(value) }}
            </span>
            <span v-else class="loyalty-empty">-</span>
          </template>

          <template #cell-outstanding_balance="{ value }">
            <span
              v-if="value != null"
              :class="value > 0 ? 'balance-debit' : 'balance-credit'"
            >
              {{ formatCurrency(value) }}
            </span>
            <span v-else class="loyalty-empty">-</span>
          </template>

          <template #cell-credit_limit="{ value }">
            <span v-if="value != null && value > 0">{{ formatCurrency(value) }}</span>
            <span v-else class="credit-limit-unlimited">Unlimited</span>
          </template>

          <template #cell-actions="{ row }">
            <div style="display: flex; gap: var(--space-2)">
              <IntercomButton size="sm" variant="ghost" @click.stop="router.push(`/customers/${row.id}/ledger`)">
                Ledger
              </IntercomButton>
              <IntercomButton size="sm" variant="ghost" @click.stop="handleEdit(row)">
                {{ $t('common.edit') }}
              </IntercomButton>
              <IntercomButton size="sm" variant="ghost" @click.stop="handleDelete(row)">
                {{ $t('common.delete') }}
              </IntercomButton>
            </div>
          </template>
        </IntercomTable>
      </IntercomCard>
      <IntercomPagination
        :offset="offset"
        :total="total"
        :limit="limit"
        @update:offset="(v) => { offset = v; fetch() }"
      />

      <!-- Create/Edit Modal -->
      <CustomerForm
        v-model:show="showCreateModal"
        :customer="selectedCustomer"
        @success="handleFormSuccess"
      />

    </div>
  </IntercomLayout>
</template>

<script setup>
import { ref, onMounted } from 'vue'
import { useRouter } from 'vue-router'
import { useI18n } from 'vue-i18n'
import { useAuth } from '@shared/composables/useAuth.js'
import { useConfirm } from '@shared/composables/useConfirm.js'
import { usePaginatedList } from '@shared/composables/usePaginatedList.js'
import { usePageShortcuts } from '@shared/composables/usePageShortcuts.js'
import { useBilling } from '../composables/useBilling.js'
import {
  IntercomLayout,
  IntercomTopbar,
  IntercomSidebar,
  IntercomButton,
  IntercomCard,
  IntercomTable,
  IntercomPagination,
} from '@shared/components/intercom'
import CustomerForm from '../components/CustomerForm.vue'
import { billingNavSections } from '../config/navigation.js'
import { formatDate } from '../utils/billingUtils.js'

const { t } = useI18n()
const router = useRouter()
const { user, logout } = useAuth()
const { confirm } = useConfirm()
const { listCustomers, deleteCustomer, businessName, fetchBusinessName } = useBilling()

const navSections = billingNavSections

// State
const {
  items: customers,
  total,
  loading: isLoading,
  offset,
  limit,
  fetch,
  resetAndFetch,
} = usePaginatedList((params) => listCustomers(params))

const showCreateModal = ref(false)
const selectedCustomer = ref(null)
const sortBy = ref('name')
const sortOrder = ref('asc')
const searchInputRef = ref(null)

// Keyboard shortcuts for list view
const { selectedIndex } = usePageShortcuts({
  items: customers,
  onNew: () => { selectedCustomer.value = null; showCreateModal.value = true },
  onOpen: (item) => handleEdit(item),
  onEdit: (item) => handleEdit(item),
  onDelete: (item) => handleDelete(item),
  searchRef: searchInputRef,
})


// Table columns
const columns = [
  { key: 'name', title: t('customers.name'), width: '180px', sortable: true, sortKey: 'name' },
  { key: 'phone', title: t('customers.phone'), width: '140px', sortable: true, sortKey: 'phone' },
  { key: 'email', title: t('customers.email'), width: '180px' },
  { key: 'loyalty_points_balance', title: t('loyalty.currentBalance', 'Loyalty Points'), width: '120px', align: 'center', sortable: true, sortKey: 'loyalty_points_balance' },
  { key: 'outstanding_balance', title: 'Outstanding', width: '130px', align: 'right', sortable: true, sortKey: 'outstanding_balance' },
  { key: 'credit_limit', title: 'Credit Limit', width: '120px', align: 'right' },
  { key: 'actions', title: 'Actions', width: '200px', align: 'right' },
]

/**
 * Format a number as INR currency
 */
const formatCurrency = (value) => {
  if (value == null) return '-'
  return new Intl.NumberFormat('en-IN', { style: 'currency', currency: 'INR', minimumFractionDigits: 2 }).format(value)
}


/**
 * Handle edit customer
 */
const handleEdit = (customer) => {
  selectedCustomer.value = customer
  showCreateModal.value = true
}

/**
 * Handle delete customer
 */
const handleDelete = async (customer) => {
  const ok = await confirm(`Delete customer "${customer.name}"?`, { title: 'Delete Customer', type: 'error' })
  if (!ok) {
    return
  }

  try {
    await deleteCustomer(customer.id)
    await fetch()
  } catch (error) {
    console.error('Failed to delete customer:', error)
  }
}

/**
 * Handle form success (create/update)
 */
const handleFormSuccess = async () => {
  showCreateModal.value = false
  selectedCustomer.value = null
  await fetch()
}

const handleSort = ({ sortBy: newSort, sortOrder: newOrder }) => {
  sortBy.value = newSort
  sortOrder.value = newOrder
  resetAndFetch({ sort_by: newSort, sort_order: newOrder })
}

const searchQuery = ref('')
const handleSearchInput = () => { resetAndFetch({ search: searchQuery.value, sort_by: sortBy.value, sort_order: sortOrder.value }) }
const clearSearch = () => { searchQuery.value = ''; resetAndFetch({ search: '', sort_by: sortBy.value, sort_order: sortOrder.value }) }

const handleNotifications = () => {
  console.log('Notifications')
}

const handleSettings = () => {
  router.push('/settings')
}

const handleLogout = async () => {
  try {
    await logout()
    router.push('/login')
  } catch (error) {
    console.error('Logout error:', error)
    router.push('/login')
  }
}

onMounted(() => {
  fetchBusinessName()
})
</script>

<style scoped>
@import '@shared/styles/intercom-tokens.css';
@import '@shared/styles/intercom-base.css';

.customers-view {
  padding: var(--space-8);
  max-width: var(--content-max-width);
  margin: 0 auto;
}

.page-header {
  margin-bottom: var(--space-8);
  display: flex;
  align-items: center;
  justify-content: space-between;
}

.page-title {
  font-size: var(--font-size-2xl);
  font-weight: var(--font-weight-bold);
  color: var(--color-charcoal);
  letter-spacing: var(--letter-spacing-tight);
  margin: 0;
}

.page-subtitle {
  font-size: var(--font-size-base);
  color: var(--color-gray-500);
  margin-top: var(--space-1);
  margin-bottom: 0;
}

/* Search Bar */
.search-bar { display: flex; gap: var(--space-3); margin-bottom: var(--space-6); }
.search-input-wrapper { flex: 1; position: relative; display: flex; align-items: center; }
.search-icon { position: absolute; left: 14px; color: var(--color-gray-400); pointer-events: none; }
.search-input { width: 100%; height: 44px; padding: 0 var(--space-10) 0 44px; font-size: var(--font-size-base); color: var(--color-charcoal); background: var(--color-white); border: 1px solid var(--color-gray-200); border-radius: var(--radius-lg); outline: none; transition: all var(--transition-fast); }
.search-input:focus { border-color: var(--color-blue); box-shadow: 0 0 0 3px rgba(0, 87, 255, 0.1); }
.search-clear { position: absolute; right: 12px; display: flex; align-items: center; justify-content: center; width: 24px; height: 24px; background: var(--color-gray-100); border: none; border-radius: 50%; cursor: pointer; color: var(--color-gray-500); transition: all var(--transition-fast); }
.search-clear:hover { background: var(--color-gray-200); color: var(--color-gray-700); }

/* Loyalty Badge */
.loyalty-badge {
  display: inline-flex;
  align-items: center;
  gap: var(--space-1);
  padding: var(--space-1) var(--space-2);
  background: linear-gradient(135deg, #fef3c7 0%, #fde68a 100%);
  color: #92400e;
  border-radius: var(--radius-full);
  font-size: var(--font-size-sm);
  font-weight: var(--font-weight-semibold);
}

.loyalty-badge svg {
  color: #f59e0b;
}

.loyalty-empty {
  color: var(--color-gray-400);
}

/* Balance colors */
.balance-debit {
  color: #dc2626;
  font-weight: var(--font-weight-medium);
}

.balance-credit {
  color: #16a34a;
  font-weight: var(--font-weight-medium);
}

.credit-limit-unlimited {
  color: var(--color-gray-400);
  font-size: var(--font-size-sm);
}


@media (max-width: 768px) {
  .customers-view {
    padding: var(--space-4);
  }

  .page-header {
    flex-direction: column;
    align-items: flex-start;
    gap: var(--space-4);
  }

  .page-title {
    font-size: 26px;
  }

}
</style>
